import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts'

interface StarRatingData {
    rating: number
    specificGravity: number
}

interface Props {
    data: StarRatingData[]
    totalComments?: number  // 從近期概況獲取的評論總數
}

export const StarRatingChart = ({ data, totalComments }: Props): JSX.Element => {
    // 使用傳入的 totalComments，若未傳入則計算百分比總和
    const total = totalComments ?? data.reduce((sum, r) => sum + r.specificGravity, 0)
    // 根據截圖調整：1星到5星使用深綠到淺綠的漸層色系
    const colors = ['#5a7563', '#6d8576', '#7da387', '#a5c5b0', '#c8dcc8']

    // 轉換資料格式給 Recharts
    const chartData = data.map((item, index) => ({
        name: `${item.rating}星`,
        value: item.specificGravity,
        rating: item.rating,
        color: colors[index]
    }))

    // 自定義外部標籤渲染函數（使用連接線方式）
    const renderCustomLabel = (props: any) => {
        const { cx, cy, midAngle, outerRadius, rating, value } = props
        const RADIAN = Math.PI / 180

        // 標籤距離圓心的距離（縮短以讓連接線更靠近）
        const radius = outerRadius + 50
        const x = cx + radius * Math.cos(-midAngle * RADIAN)
        const y = cy + radius * Math.sin(-midAngle * RADIAN)

        // 星星圖示
        const stars = Array.from({ length: 5 }, (_, i) => i + 1)

        return (
            <g>
                {/* 百分比文字 - 在上方 */}
                <text
                    x={x}
                    y={y - 10}
                    fill="#373737"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="[font-family:'Noto_Sans_TC',Helvetica] font-bold"
                    fontSize="16"
                >
                    {value}%
                </text>

                {/* 星級圖示 - 在下方 */}
                {stars.map((star, index) => {
                    const starX = x - 30 + index * 12
                    const starY = y + 5
                    const isFilled = star <= rating
                    return (
                        <g key={star}>
                            <polygon
                                points={`${starX},${starY - 4} ${starX + 2.5},${starY + 1} ${starX + 7},${starY + 1} ${starX + 3.5},${starY + 4} ${starX + 5},${starY + 8.5} ${starX},${starY + 6} ${starX - 5},${starY + 8.5} ${starX - 3.5},${starY + 4} ${starX - 7},${starY + 1} ${starX - 2.5},${starY + 1}`}
                                fill={isFilled ? '#e8d080' : '#d1d5db'}
                                stroke="none"
                            />
                        </g>
                    )
                })}
            </g>
        )
    }

    return (
        <div className="relative w-full h-full flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={75}
                        paddingAngle={2}
                        dataKey="value"
                        label={renderCustomLabel}
                        labelLine={{ stroke: '#999', strokeWidth: 1 }}
                        animationBegin={0}
                        animationDuration={800}
                        startAngle={90}
                        endAngle={450}
                    >
                        {chartData.map((entry, index) => (
                            <Cell
                                key={`cell-${index}`}
                                fill={entry.color}
                                stroke="white"
                                strokeWidth={3}
                            />
                        ))}
                    </Pie>

                    {/* 中心文字 - 使用單獨的 text 元素 */}
                    <text
                        x="50%"
                        y="48%"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        className="[font-family:'Noto_Sans_TC',Helvetica] font-bold"
                        fontSize="13"
                        fill="#373737"
                    >
                        總評論數
                    </text>
                    <text
                        x="50%"
                        y="55%"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        className="[font-family:'Noto_Sans_TC',Helvetica] font-bold"
                        fontSize="20"
                        fill="#373737"
                    >
                        {total}
                    </text>
                </PieChart>
            </ResponsiveContainer>
        </div>
    )
}
